'use client';

import { useEffect, useMemo } from 'react';

import {
  dispatchGTMEvent,
  usePersonalizationTracker,
  useCallbackRef,
  type GTMCustomEvent,
  type GTMEvent,
  type GTMPurchaseParams,
} from '@skin-seoul/utils';

import {
  ANALYTICS_EVENT_TYPE,
  createAnalyticsEventId,
  type AnalyticsContext,
  type AnalyticsEventRequest,
  type AnalyticsEventType,
  type CheckoutContext,
  type ConsentState,
  type CustomEventContext,
  type InteractionContext,
  type ProductContext,
} from '../../infrastructure/analytics';
import { getServiceLogger } from '../../infrastructure/common/serviceLogger';
import {
  mapAddToCart,
  mapBeginCheckout,
  mapPurchase,
  mapViewItem,
  type TrackAddToCartGtmExtras,
  type TrackBeginCheckoutGtmExtras,
  type TrackViewGtmExtras,
} from '../../infrastructure/analytics/utils/gtmEventMapper';
import { useAnalyticsPlatformContext } from '../../context/analyticsPlatformContext';
import { publishAnalyticsDebugEvent } from './analyticsDebugBus';
import { normalizeAnalyticsIdentifier, readActorIdCookie } from './analyticsIdentity';
import { useAnalyticsService } from './useAnalyticsService';

// Module-scope so all useAnalyticsEvent instances share the same identity state
// and we only push user_identity_ready when the userId actually transitions.
// Mirrors master's GTMUserIdentify ref-based dedup (single mount in layout.tsx).
const _lastIdentifiedUserId: { current: string | null | undefined } = { current: undefined };

export interface AnalyticsPlatformContext {
  platform: 'Web' | 'Android' | 'iOS';
  clientId?: string;
  userId?: string;
  pagePath?: string;
  pageTitle?: string;
  pageReferrer?: string;
  consent?: ConsentState;
  osName?: string;
  deviceModel?: string;
  appVersion?: string;
  sessionId?: string;
  /** Firebase Analytics app instance ID, injected by the app shell inside the WebView. */
  appInstanceId?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  utmId?: string;
  gadSource?: string;
  gclid?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  ttclid?: string;
  ttp?: string;
  msclkid?: string;
  wbraid?: string;
  gbraid?: string;
  getMixpanelReplayId?: () => string | undefined;
  /**
   * When true, events are logged to console instead of sent to server.
   * Used in local development to avoid polluting analytics with dev data.
   */
  debugMode?: boolean;
  /**
   * When true, every event is echoed to the console and to `analyticsDebugBus` **as well as**
   * being sent. Bound to the DEV environment so a deployed dev build can be verified; unlike
   * `debugMode` it never suppresses the send.
   */
  verboseLogging?: boolean;
  /**
   * Called after each analytics event is sent.
   * Used by mobile to clear one-time UTM after first event (match web behavior).
   */
  onEventSent?: () => void;
}

export interface TrackViewParams {
  productId?: string;
  pageType?: string;
  /** Extra params merged into event_properties via customEvent.params on the server */
  params?: Record<string, unknown>;
  gtm?: TrackViewGtmExtras;
}

export interface TrackBrowsingParams {
  pageType?: string;
  /** Extra params merged into event_properties via customEvent.params on the server */
  params?: Record<string, unknown>;
}

export interface TrackAddToCartParams {
  productId: string;
  quantity?: number;
  variant?: string;
  checkout?: CheckoutContext;
  sectionName?: string;
  /** 0-based slot of the product within its section. */
  position?: number;
  /** Extra attribution params merged into event properties by EventAggregator. */
  params?: Record<string, unknown>;
  gtm?: TrackAddToCartGtmExtras;
}

export interface TrackRemoveFromCartParams {
  productId: string;
  quantity?: number;
  variant?: string;
}

export interface TrackPurchaseParams {
  orderId: string;
  checkout?: CheckoutContext;
  gtm?: GTMPurchaseParams;
}

export interface TrackBeginCheckoutParams {
  checkout: {
    total: number;
    currency: string;
    cartProducts: Array<Required<Pick<ProductContext, 'id' | 'quantity'>>>;
  };
  gtm?: TrackBeginCheckoutGtmExtras;
}

export interface TrackImpressionParams {
  productId: string;
  sectionName?: string;
  /** 0-based slot of the card within its list. */
  position?: number;
  /** Extra attribution params merged into event properties by EventAggregator. */
  params?: Record<string, unknown>;
}

export interface TrackClickParams {
  productId: string;
  sectionName?: string;
  /** 0-based slot of the card within its list — for recommendation-ranking analysis. */
  position?: number;
  /** Extra attribution params merged into event properties by EventAggregator. */
  params?: Record<string, unknown>;
}

export interface TrackAuthParams {
  authenticationMethod: string;
  /**
   * Pass the freshly authenticated user's id straight from the auth response.
   * `resolvedContext.userId` (read from `useUser()`) is stale at this exact
   * callback tick — the user query has just been invalidated but the refetch
   * hasn't resolved. In RN WebView the cookie-based server-side fallback is
   * also unreliable, which is why Android `signed_up`/`logged_in` events
   * were landing without `user_id`.
   */
  userId?: string;
}

export interface TrackCustomParams {
  /** Channel-neutral canonical name sent to EA, Mixpanel, and BigQuery. */
  eventName?: string;
  gtmEventName?: string;
  klaviyoMetricName?: string;
  params?: Record<string, unknown>;
  /** Client idempotency key — EA dedupes retries of the same logical event. */
  uniqueId?: string;
  /**
   * Set to `true` when the caller is handling its own `dispatchGTMEvent` push
   * (e.g. needs a master flat shape like `{event: 'page_view', event_name:
   * 'begin_checkout', ...}`). `trackCustom`'s default GTM passthrough uses
   * `{event: gtmEventName, ...params}`, which would duplicate the push.
   */
  skipGTM?: boolean;
}

const buildInteractionContext = ({
  sectionName,
  position,
}: Pick<InteractionContext, 'sectionName' | 'position'>): InteractionContext | undefined => {
  if (sectionName == null && position == null) {
    return undefined;
  }

  return {
    ...(sectionName != null ? { sectionName } : {}),
    ...(position != null ? { position } : {}),
  };
};

const pushGTMEvent = (event: GTMEvent | GTMCustomEvent): void => {
  try {
    dispatchGTMEvent(event);
  } catch (error) {
    // Never let a GTM failure break the server sendEvent path.
    getServiceLogger().error('[useAnalyticsEvent] dispatchGTMEvent failed', error);
  }
};

const pushGoogleGTMEvent = (event: GTMCustomEvent): void => {
  pushGTMEvent(event);
  pushGTMEvent({ transaction_id: null } as unknown as GTMCustomEvent);
};

export function useAnalyticsEvent() {
  const resolvedContext = useAnalyticsPlatformContext();
  const tracker = usePersonalizationTracker();
  const { useTrackEvent } = useAnalyticsService();
  const { mutate } = useTrackEvent();

  const sendEvent = useCallbackRef(
    (
      eventType: AnalyticsEventType,
      analytics: Omit<
        AnalyticsContext,
        'clientId' | 'pagePath' | 'pageTitle' | 'pageReferrer' | 'platform' | 'consent'
      > = {},
      options?: Pick<AnalyticsEventRequest, 'productId' | 'orderId' | 'uniqueId' | 'services'>,
    ) => {
      // Try in-memory actorId first, fall back to cookie if PersonalizationClient hasn't initialized yet
      const actorId = normalizeAnalyticsIdentifier(tracker.getActorId()) ?? readActorIdCookie();

      if (!actorId) {
        return;
      }

      // Read pagePath/pageTitle at send time (not render time) to avoid stale values during SPA navigation
      const currentPagePath =
        typeof window !== 'undefined' && window.location ? window.location.href : resolvedContext.pagePath;
      const currentPageTitle =
        typeof window !== 'undefined' && typeof document !== 'undefined' ? document.title : resolvedContext.pageTitle;
      const sessionId = analytics.sessionId ?? resolvedContext.sessionId ?? tracker.getSessionInfo().sessionId;

      const request: AnalyticsEventRequest = {
        actorId,
        eventType,
        analytics: {
          clientId: normalizeAnalyticsIdentifier(resolvedContext.clientId) ?? actorId,
          sessionId,
          appInstanceId: resolvedContext.appInstanceId,
          userId: resolvedContext.userId,
          pagePath: currentPagePath,
          pageTitle: currentPageTitle,
          pageReferrer: resolvedContext.pageReferrer,
          platform: resolvedContext.platform,
          consent: resolvedContext.consent,
          osName: resolvedContext.osName,
          deviceModel: resolvedContext.deviceModel,
          appVersion: resolvedContext.appVersion,
          utmSource: resolvedContext.utmSource,
          utmMedium: resolvedContext.utmMedium,
          utmCampaign: resolvedContext.utmCampaign,
          utmContent: resolvedContext.utmContent,
          utmTerm: resolvedContext.utmTerm,
          utmId: resolvedContext.utmId,
          gadSource: resolvedContext.gadSource,
          gclid: resolvedContext.gclid,
          fbclid: resolvedContext.fbclid,
          fbc: resolvedContext.fbc,
          fbp: resolvedContext.fbp,
          ttclid: resolvedContext.ttclid,
          ttp: resolvedContext.ttp,
          msclkid: resolvedContext.msclkid,
          wbraid: resolvedContext.wbraid,
          gbraid: resolvedContext.gbraid,
          mixpanelReplayId: resolvedContext.getMixpanelReplayId?.(),
          ...analytics,
        },
        ...options,
      };

      // Ahead of the debugMode branch so a local run echoes the same payload a deployed dev build
      // does, rather than only the short summary below.
      if (resolvedContext.verboseLogging) {
        const customEvent = request.analytics.customEvent;
        const debugEvent = {
          eventType,
          eventName: customEvent?.eventName ?? customEvent?.gtmEventName,
          params: customEvent?.params,
          platform: request.analytics.platform ?? 'unknown',
          occurredAt: Date.now(),
        };
        getServiceLogger().info(`[Analytics] ${debugEvent.eventName ?? eventType}`, debugEvent.params ?? {});
        publishAnalyticsDebugEvent(debugEvent);
      }

      if (resolvedContext.debugMode) {
        getServiceLogger().debug(`[Analytics] ${eventType}`, {
          eventType,
          productId: options?.productId,
          orderId: options?.orderId,
          uniqueId: options?.uniqueId,
          platform: resolvedContext.platform,
          hasCheckout: Boolean(analytics.checkout),
        });
        resolvedContext.onEventSent?.();
        return;
      }

      mutate(request);
      resolvedContext.onEventSent?.();
    },
  );

  // GTM dispatch is gated on `window.dataLayer` existence inside dispatchGTMEvent.
  // - Browser + WebView: dataLayer loaded by GTM → push fires.
  // - RN mobile shell: no dataLayer → silent no-op.
  // No platform-based hook-level gate needed.

  const api = useMemo(
    () => ({
      trackView: ({ productId, pageType, params, gtm }: TrackViewParams = {}) => {
        const uniqueId = pageType === 'PDP' && productId ? createAnalyticsEventId() : undefined;
        sendEvent(
          ANALYTICS_EVENT_TYPE.VIEW,
          {
            pageType,
            ...(params ? { customEvent: { params } } : {}),
          },
          { productId, uniqueId },
        );

        // Master pushed view_item only on PDP; category pages inherited the
        // root-level page_view push from layout.client LoggingView.
        if (pageType === 'PDP' && productId && uniqueId) {
          pushGoogleGTMEvent(mapViewItem({ productId, uniqueId }, gtm));
        }
      },

      trackAddToCart: ({
        productId,
        quantity,
        variant,
        checkout,
        sectionName,
        position,
        params,
        gtm,
      }: TrackAddToCartParams) => {
        const uniqueId = createAnalyticsEventId();
        const product: ProductContext = { id: productId, quantity, variant };
        const interaction = buildInteractionContext({ sectionName, position });
        sendEvent(
          ANALYTICS_EVENT_TYPE.ADD_TO_CART,
          {
            product,
            ...(checkout ? { checkout } : {}),
            ...(interaction ? { interaction } : {}),
            ...(params ? { customEvent: { params } } : {}),
          },
          { productId, uniqueId },
        );

        pushGoogleGTMEvent(mapAddToCart({ productId, quantity, variant, uniqueId }, gtm));
      },

      trackRemoveFromCart: ({ productId, quantity, variant }: TrackRemoveFromCartParams) => {
        const product: ProductContext = { id: productId, quantity, variant };
        sendEvent(ANALYTICS_EVENT_TYPE.REMOVE_FROM_CART, { product }, { productId });
        // No GTM dispatch: the live container has no trigger listening for `remove_from_cart`.
      },

      trackPurchase: ({ orderId, checkout, gtm }: TrackPurchaseParams) => {
        sendEvent(ANALYTICS_EVENT_TYPE.PURCHASE, { checkout }, { orderId });

        if (!gtm) {
          return;
        }
        pushGTMEvent(mapPurchase({ orderId, ...gtm }));
      },

      trackBeginCheckout: ({ checkout, gtm }: TrackBeginCheckoutParams) => {
        const sessionId = resolvedContext.sessionId ?? tracker.getSessionInfo().sessionId;
        sendEvent(
          ANALYTICS_EVENT_TYPE.CUSTOM,
          {
            sessionId,
            checkout,
            customEvent: { eventName: 'begin_checkout' },
          },
          { services: ['paidmedia', 'firebase'] },
        );
        pushGoogleGTMEvent(mapBeginCheckout({ checkout, sessionId }, gtm));
      },

      trackImpression: ({ productId, sectionName, position, params }: TrackImpressionParams) => {
        const interaction = buildInteractionContext({ sectionName, position });
        sendEvent(
          ANALYTICS_EVENT_TYPE.IMPRESSION,
          { interaction, ...(params ? { customEvent: { params } } : {}) },
          { productId },
        );
        // Server-only: generic trackImpression does not push to dataLayer.
        // Specific named impressions go via trackCustom({gtmEventName: '<name>', ...}).
      },

      trackClick: ({ productId, sectionName, position, params }: TrackClickParams) => {
        const interaction = buildInteractionContext({ sectionName, position });
        sendEvent(
          ANALYTICS_EVENT_TYPE.CLICK,
          { interaction, ...(params ? { customEvent: { params } } : {}) },
          { productId },
        );
        // Server-only: generic trackClick does not push to dataLayer.
        // Specific named clicks go via trackCustom({gtmEventName: '<name>', ...}).
      },

      trackHomePageView: () => {
        // GTM page_view push is handled centrally by GTMPageViewTracker at
        // the root layout on every SPA navigation. This method stays
        // server-only so we don't double-push.
        sendEvent(ANALYTICS_EVENT_TYPE.HOME_PAGE_VIEW);
      },

      trackBrowsing: ({ pageType, params }: TrackBrowsingParams = {}) => {
        // Same as above — GTM page_view is centralized.
        sendEvent(ANALYTICS_EVENT_TYPE.BROWSING, {
          pageType,
          ...(params ? { customEvent: { params } } : {}),
        });
      },

      trackLogin: ({ authenticationMethod, userId }: TrackAuthParams) => {
        // Master had no direct GTM push for login/sign_up. GTM picks these
        // up via user_identity_ready + enhanced measurement. Keep server
        // event only.
        const interaction: InteractionContext = { authenticationMethod };
        sendEvent(ANALYTICS_EVENT_TYPE.LOGIN, { interaction, ...(userId ? { userId } : {}) });
      },

      trackSignup: ({ authenticationMethod, userId }: TrackAuthParams) => {
        const interaction: InteractionContext = { authenticationMethod };
        sendEvent(ANALYTICS_EVENT_TYPE.SIGNUP, { interaction, ...(userId ? { userId } : {}) });
      },

      // GTM-only events (engaged_60s / reward_granted / express_checkout_click /
      // and all Tier 3 click triggers such as 'Wishlist Click', 'begin_checkout',
      // etc.) flow through this method. It has no server-side enrichment path —
      // it's a passthrough to dataLayer using the exact `event: gtmEventName`
      // literal so the corresponding GTM Custom Event trigger fires.
      trackCustom: ({ eventName, gtmEventName, klaviyoMetricName, params, uniqueId, skipGTM }: TrackCustomParams) => {
        const customEvent: CustomEventContext = {
          eventName,
          gtmEventName,
          klaviyoMetricName,
          params,
        };
        sendEvent(ANALYTICS_EVENT_TYPE.CUSTOM, { customEvent }, uniqueId ? { uniqueId } : undefined);

        if (gtmEventName && !skipGTM) {
          pushGTMEvent({ event: gtmEventName, ...(params ?? {}) });
        }
      },
    }),
    [resolvedContext.sessionId, sendEvent, tracker],
  );

  // Push user_identity_ready whenever userId transitions (login, logout, or
  // user switch). Module-scope ref dedupes across multiple useAnalyticsEvent
  // instances — mirrors the master GTMUserIdentify.tsx useRef pattern, which
  // was mounted once at the root layout.
  useEffect(() => {
    const nextUserId = resolvedContext.userId ?? null;
    if (_lastIdentifiedUserId.current === nextUserId) {
      return;
    }
    _lastIdentifiedUserId.current = nextUserId;
    pushGTMEvent({
      event: 'user_identity_ready',
      user_id: nextUserId,
      user_properties: {
        login_status: nextUserId ? 'logged_in' : 'logged_out',
      },
    });
  }, [resolvedContext.userId]);

  return api;
}
